import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { 
  Couple, 
  Category, 
  Expense, 
  Challenge, 
  Achievement,
  ExpenseSummary,
  MonthlySpending,
  ChallengeProgress
} from '../../types/database';

// Async thunk for initializing couple data
export const initializeCoupleData = createAsyncThunk(
  'database/initializeCouple',
  async (coupleData: { partner1_name: string; partner2_name: string }) => {
    const response = await fetch('/api/couples/initialize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(coupleData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to initialize couple');
    }
    
    return response.json();
  }
);

// Async thunk for fetching couple data
export const fetchCoupleData = createAsyncThunk(
  'database/fetchCouple',
  async (coupleId: string) => {
    const response = await fetch(`/api/couples/${coupleId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch couple data');
    }
    
    return response.json();
  }
);

// Async thunk for fetching expenses
export const fetchExpenses = createAsyncThunk(
  'database/fetchExpenses',
  async (coupleId: string) => {
    const response = await fetch(`/api/expenses/${coupleId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch expenses');
    }
    
    return response.json();
  }
);

// Async thunk for adding expense
export const addExpense = createAsyncThunk(
  'database/addExpense',
  async (expenseData: {
    couple_id: string;
    category_id: string;
    amount: number;
    description?: string;
    expense_date: string;
    added_by: string;
  }) => {
    const response = await fetch('/api/expenses/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(expenseData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to add expense');
    }
    
    return response.json();
  }
);

// Async thunk for fetching challenges
export const fetchChallenges = createAsyncThunk(
  'database/fetchChallenges',
  async (coupleId: string) => {
    const response = await fetch(`/api/challenges/${coupleId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch challenges');
    }
    
    return response.json();
  }
);

interface DatabaseState {
  // Current couple data
  currentCouple: Couple | null;
  
  // Categories
  categories: Category[];
  
  // Expenses
  expenses: Expense[];
  expenseSummary: ExpenseSummary | null;
  monthlySpending: MonthlySpending[];
  
  // Challenges
  challenges: Challenge[];
  challengeProgress: ChallengeProgress[];
  
  // Achievements
  achievements: Achievement[];
  
  // Loading states
  loading: {
    couple: boolean;
    categories: boolean;
    expenses: boolean;
    challenges: boolean;
    achievements: boolean;
    addExpense: boolean;
    initialize: boolean;
  };
  
  // Error states
  errors: {
    couple: string | null;
    categories: string | null;
    expenses: string | null;
    challenges: string | null;
    achievements: string | null;
    addExpense: string | null;
    initialize: string | null;
  };
}

const initialState: DatabaseState = {
  currentCouple: null,
  categories: [],
  expenses: [],
  expenseSummary: null,
  monthlySpending: [],
  challenges: [],
  challengeProgress: [],
  achievements: [],
  loading: {
    couple: false,
    categories: false,
    expenses: false,
    challenges: false,
    achievements: false,
    addExpense: false,
    initialize: false,
  },
  errors: {
    couple: null,
    categories: null,
    expenses: null,
    challenges: null,
    achievements: null,
    addExpense: null,
    initialize: null,
  },
};

const databaseSlice = createSlice({
  name: 'database',
  initialState,
  reducers: {
    // Clear all data (for logout)
    clearAllData: (state) => {
      return initialState;
    },
    
    // Clear specific errors
    clearError: (state, action: PayloadAction<keyof DatabaseState['errors']>) => {
      state.errors[action.payload] = null;
    },
    
    // Update couple points
    updateCouplePoints: (state, action: PayloadAction<{
      partner1_points: number;
      partner2_points: number;
      total_points: number;
    }>) => {
      if (state.currentCouple) {
        state.currentCouple.partner1_points = action.payload.partner1_points;
        state.currentCouple.partner2_points = action.payload.partner2_points;
        state.currentCouple.total_points = action.payload.total_points;
      }
    },
    
    // Update category usage
    updateCategoryUsage: (state, action: PayloadAction<{
      categoryId: string;
      increment: number;
    }>) => {
      const category = state.categories.find(c => c.id === action.payload.categoryId);
      if (category) {
        category.usage_count += action.payload.increment;
      }
    },
    
    // Add new achievement
    addAchievement: (state, action: PayloadAction<Achievement>) => {
      state.achievements.unshift(action.payload);
    },
    
    // Update challenge progress
    updateChallengeProgress: (state, action: PayloadAction<{
      challengeId: string;
      progress: number;
      status?: string;
    }>) => {
      const challenge = state.challenges.find(c => c.id === action.payload.challengeId);
      if (challenge) {
        challenge.current_progress = action.payload.progress;
        if (action.payload.status) {
          challenge.status = action.payload.status as any;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Initialize couple
    builder
      .addCase(initializeCoupleData.pending, (state) => {
        state.loading.initialize = true;
        state.errors.initialize = null;
      })
      .addCase(initializeCoupleData.fulfilled, (state, action) => {
        state.loading.initialize = false;
        state.currentCouple = action.payload.couple;
        state.categories = action.payload.categories;
      })
      .addCase(initializeCoupleData.rejected, (state, action) => {
        state.loading.initialize = false;
        state.errors.initialize = action.error.message || 'Failed to initialize couple';
      });
    
    // Fetch couple data
    builder
      .addCase(fetchCoupleData.pending, (state) => {
        state.loading.couple = true;
        state.errors.couple = null;
      })
      .addCase(fetchCoupleData.fulfilled, (state, action) => {
        state.loading.couple = false;
        state.currentCouple = action.payload.couple;
        if (action.payload.categories) {
          state.categories = action.payload.categories;
        }
      })
      .addCase(fetchCoupleData.rejected, (state, action) => {
        state.loading.couple = false;
        state.errors.couple = action.error.message || 'Failed to fetch couple data';
      });
    
    // Fetch expenses
    builder
      .addCase(fetchExpenses.pending, (state) => {
        state.loading.expenses = true;
        state.errors.expenses = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading.expenses = false;
        state.expenses = action.payload.expenses;
        if (action.payload.summary) {
          state.expenseSummary = action.payload.summary;
        }
        if (action.payload.monthlySpending) {
          state.monthlySpending = action.payload.monthlySpending;
        }
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading.expenses = false;
        state.errors.expenses = action.error.message || 'Failed to fetch expenses';
      });
    
    // Add expense
    builder
      .addCase(addExpense.pending, (state) => {
        state.loading.addExpense = true;
        state.errors.addExpense = null;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.loading.addExpense = false;
        state.expenses.unshift(action.payload.expense);
        
        // Update couple points if provided
        if (action.payload.updatedCouple) {
          state.currentCouple = action.payload.updatedCouple;
        }
        
        // Add any new achievements
        if (action.payload.newAchievements) {
          state.achievements.unshift(...action.payload.newAchievements);
        }
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.loading.addExpense = false;
        state.errors.addExpense = action.error.message || 'Failed to add expense';
      });
    
    // Fetch challenges
    builder
      .addCase(fetchChallenges.pending, (state) => {
        state.loading.challenges = true;
        state.errors.challenges = null;
      })
      .addCase(fetchChallenges.fulfilled, (state, action) => {
        state.loading.challenges = false;
        state.challenges = action.payload.challenges;
        if (action.payload.progress) {
          state.challengeProgress = action.payload.progress;
        }
      })
      .addCase(fetchChallenges.rejected, (state, action) => {
        state.loading.challenges = false;
        state.errors.challenges = action.error.message || 'Failed to fetch challenges';
      });
  },
});

export const {
  clearAllData,
  clearError,
  updateCouplePoints,
  updateCategoryUsage,
  addAchievement,
  updateChallengeProgress,
} = databaseSlice.actions;

export default databaseSlice.reducer;